import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { ContainerWrapper, Content } from '../../components/GridComponent/AdminLte.js';
import PageLoading from "../PageLoading";
import Wrapper from "../../components/Template/Wrapper";
import Authenticator from "../../services/Authenticator";
import BroadcastModal from "../../components/Broadcasts/Queue/BroadcastModal";
import FormBody from "../../components/Broadcasts/Queue/FormBody";
import AudienceBuilder from "./AudienceBuilder";
import QuestionBuilder from "./QuestionBuilder";
import EmployeeListUpload from "./EmployeeListUpload";
import { ContentSection, MediaSection, TimingSection, BehaviourSection, Select, Text } from "./BroadcastFormSections";
import {
  defaultValues, valuesFromItem, buildFormData, checkMediaFile, checkQuestions, checkCustomKey, draftItem, errorMessages, DEFAULT_LIMITS,
} from "./broadcastFormHelpers";
import {
  fetchBroadcastLookups, fetchBroadcast, clearBroadcastInstance, createBroadcast, updateBroadcast, previewBroadcast,
} from '../../store/actions/broadcasts/broadcastActions';
import "./Broadcasts.css";

/**
 * Announcements -> Post (new) and Manage -> Edit (existing). One Formik form in seven sections:
 * Content, Media, Audience, Timing, Behaviour, Questions (kind = form), Preview. A custom item
 * (kind = custom, phase 3) picks its registry key in Content, has no Questions and takes the
 * employee-list upload like a form (SPEC 4.3 "forms/custom only").
 *
 * Submit builds the multipart payload (broadcastFormHelpers.buildFormData) and posts it to
 * /broadcasts or /broadcasts/{id}/update; a 422 lists the server's messages at the top of the
 * form, anything else keeps the form as it was. A form's questions are checked client-side
 * first (checkQuestions) and nothing is sent while they fail.
 */

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required("Title is required").max(255, "Title is too long"),
});

class BroadcastForm extends Component {

  state = {
    ready: false,          // lookups (and the item, when editing) answered
    saving: false,
    mediaError: null,      // client-side file check message
    questionError: null,   // client-side question check message (kind = form)
    serverErrors: [],      // 422 messages
    preview: { country_id: "", department_id: "", level_id: "", days_since_hire: "" },
    previewResult: null,   // {matches, reasons}
    previewItem: null,     // draft rendered by BroadcastModal
  };

  itemId = () => (this.props.params && this.props.params.id ? this.props.params.id : null);
  isHr = () => Authenticator.scanLevel("HR");

  componentDidMount() {
    const id = this.itemId();
    this.props.clearBroadcastInstance();
    Promise.all([
      this.props.fetchBroadcastLookups(),
      id ? this.props.fetchBroadcast(id) : null,
    ]).then(() => this.setState({ ready: true }));
  }

  goToList = () => this.props.history.push(global.links.broadcast_manage);

  // Runs the type / size check as soon as a file is chosen; Save sends nothing while it fails.
  onMediaFile = (values, setFieldValue) => (file) => {
    setFieldValue("media_file", file);
    this.setState({ mediaError: checkMediaFile(values.media_type, file, this.limits()) });
  }

  limits = () => ({ ...DEFAULT_LIMITS, ...((this.props.lookups && this.props.lookups.limits) || {}) });

  onSubmit = (values) => {
    if (this.state.saving || this.state.mediaError) return;
    const questionError = checkQuestions(values) || checkCustomKey(values);
    if (questionError) {
      this.setState({ questionError: questionError, serverErrors: [] });
      return;
    }
    this.setState({ saving: true, questionError: null, serverErrors: [] });
    const id = this.itemId();
    const fd = buildFormData(values, this.props.lookups);
    const request = id ? this.props.updateBroadcast(id, fd) : this.props.createBroadcast(fd);
    request
      .then(() => this.goToList())
      .catch((e) => this.setState({ saving: false, serverErrors: errorMessages(e) }));
  }

  onCheckAudience = () => {
    const ctx = {};
    Object.keys(this.state.preview).forEach((key) => {
      if (this.state.preview[key] !== "") ctx[key] = this.state.preview[key];
    });
    this.props.previewBroadcast(this.itemId(), ctx)
      .then((result) => this.setState({ previewResult: result }))
      .catch(() => {});
  }

  setPreview = (key, value) => this.setState({ preview: { ...this.state.preview, [key]: value } });

  renderPreviewSection = (values) => {
    const { lookups } = this.props;
    const { preview, previewResult } = this.state;
    const options = (list, labelKey = "name") => [["", "—"]].concat((list || []).map((x) => [String(x.id), x[labelKey]]));
    return (
      <>
        <h4>Preview</h4>
        {this.itemId() ? (
          <>
            <div className="form-row">
              <div className="col-md-3"><Select id="preview-country" label="Preview as country" value={preview.country_id} onChange={(v) => this.setPreview("country_id", v)} options={options(lookups.countries)} /></div>
              <div className="col-md-3"><Select id="preview-department" label="Preview as department" value={preview.department_id} onChange={(v) => this.setPreview("department_id", v)} options={options(lookups.departments)} /></div>
              <div className="col-md-3"><Select id="preview-level" label="Preview as level" value={preview.level_id} onChange={(v) => this.setPreview("level_id", v)} options={options(lookups.levels)} /></div>
              <div className="col-md-3"><Text id="preview-days" label="Preview days since hire" type="number" value={preview.days_since_hire} onChange={(v) => this.setPreview("days_since_hire", v)} /></div>
            </div>
            <Button variant="outline-secondary" type="button" onClick={this.onCheckAudience}>Check audience</Button>
            {previewResult ? (
              <div role="status" className="mt-2">
                <strong>{previewResult.matches ? "Matches" : "Does not match"}</strong>
                {previewResult.reasons && previewResult.reasons.length ? (
                  <ul>{previewResult.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-muted">Save first to preview</p>
        )}
        <div className="mt-2">
          <Button variant="outline-secondary" type="button" onClick={() => this.setState({ previewItem: draftItem(values, this.props.instance) })}>
            Render preview
          </Button>
        </div>
      </>
    );
  }

  renderForm = () => {
    const { lookups, instance, user } = this.props;
    const id = this.itemId();
    const isHr = this.isHr();
    const initialValues = id ? valuesFromItem(instance, user, lookups, isHr) : defaultValues(user);
    const timezones = (lookups.timezones || []).slice();
    if (!timezones.includes(initialValues.timezone)) timezones.unshift(initialValues.timezone);
    const errors = [this.state.mediaError, this.state.questionError].filter(Boolean).concat(this.state.serverErrors);
    const form = instance && instance.form ? instance.form : null;
    const listCount = instance && Array.isArray(instance.audience) ? instance.audience.filter((r) => r.rule_type === "employee").length : 0;

    return (
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={this.onSubmit}>
        {({ values, errors: fieldErrors, touched, setFieldValue, handleSubmit }) => (
          <form className="broadcast-form" onSubmit={handleSubmit} noValidate>
            {errors.length ? (
              <div role="alert" className="alert alert-danger">
                <ul className="mb-0">{errors.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            ) : null}
            {touched.title && fieldErrors.title ? <div className="text-danger">{fieldErrors.title}</div> : null}

            <ContentSection values={values} set={setFieldValue} customForms={lookups.custom_forms} isHr={isHr} />
            <MediaSection values={values} set={setFieldValue} lookups={lookups} limits={this.limits()} onFile={this.onMediaFile(values, setFieldValue)} />
            <h4>Audience</h4>
            <AudienceBuilder values={values} setFieldValue={setFieldValue} lookups={lookups} isHr={isHr} />
            {values.kind === "form" || values.kind === "custom" ? (
              <>
                <h6>Employee list</h6>
                <EmployeeListUpload itemId={id} listCount={id ? listCount : 0} />
              </>
            ) : null}
            <TimingSection values={values} set={setFieldValue} timezones={timezones} />
            <BehaviourSection values={values} set={setFieldValue} isHr={isHr} />
            {values.kind === "form" ? (
              <>
                <h4>Questions</h4>
                <QuestionBuilder
                  questions={values.questions}
                  onChange={(questions) => setFieldValue("questions", questions)}
                  hasResponses={!!(form && Number(form.responses_count) > 0)}
                  version={form ? form.version : 1}
                  perPerson={!!values.per_person}
                />
              </>
            ) : null}
            {this.renderPreviewSection(values)}

            <div className="broadcast-form-actions">
              <Button type="submit" variant="primary" disabled={this.state.saving}>Save</Button>
              <Button type="button" variant="secondary" disabled={this.state.saving} onClick={this.goToList}>Cancel</Button>
            </div>
          </form>
        )}
      </Formik>
    );
  }

  render() {
    const { lookups, instance, instanceError } = this.props;
    const id = this.itemId();
    const title = id ? "Edit announcement" : "Post an announcement";
    let body;
    if (!this.state.ready) {
      body = <PageLoading />;
    } else if (!lookups) {
      body = <p>Couldn't load the form right now.</p>;
    } else if (id && (instanceError || !instance || String(instance.id) !== String(id))) {
      body = <p>This announcement is not available to you</p>;
    } else {
      body = this.renderForm();
    }

    return <Wrapper {...this.props} >
      <ContainerWrapper>
        <Content col="12" title={title}>
          {body}
        </Content>
        {this.state.previewItem ? (
          <BroadcastModal
            preview
            item={this.state.previewItem}
            onDismiss={() => this.setState({ previewItem: null })}
            onComplete={() => { this.setState({ previewItem: null }); return Promise.resolve(true); }}
            onAck={() => {}}
            onWatched={() => {}}
          >
            {this.state.previewItem.kind === "form" ? (
              <FormBody item={this.state.previewItem} submitResponse={() => Promise.resolve({ ok: true })} />
            ) : null}
          </BroadcastModal>
        ) : null}
      </ContainerWrapper>
    </Wrapper>;
  }
}

const mapStateToProps = (state) => {
  return {
    user           : state.user,
    lookups        : state.broadcasts.lookups,
    instance       : state.broadcasts.instance,
    instanceError  : state.broadcasts.instanceError,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    fetchBroadcastLookups   : () => dispatch( fetchBroadcastLookups() ),
    fetchBroadcast          : ( id ) => dispatch( fetchBroadcast( id ) ),
    clearBroadcastInstance  : () => dispatch( clearBroadcastInstance() ),
    createBroadcast         : ( fd ) => dispatch( createBroadcast( fd ) ),
    updateBroadcast         : ( id, fd ) => dispatch( updateBroadcast( id, fd ) ),
    previewBroadcast        : ( id, ctx ) => dispatch( previewBroadcast( id, ctx ) ),
  }
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BroadcastForm));
